import React, { useMemo } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { SocialAccount } from 'types/influencer';
import { formatNumber } from 'utils/helpers';
import '../style.scss';

interface SocialAccountReelRatesProps {
    socialAccount: SocialAccount;
}

interface ReelRate {
    id: string;
    label: string;
    value: string | null;
    tooltip: string;
}

const formatRate = (value?: string | null): string | null => {
    if (value == null) return null;
    const raw = String(value).trim().replace(/%$/, '');
    if (!raw) return null;
    const num = Number(raw);
    if (!Number.isFinite(num)) return null;
    const rounded = Math.round(num * 10) / 10;
    const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    return `${text}%`;
};

const parseCount = (value?: number | string | null): number | null => {
    if (value == null || value === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num;
};

const formatCount = (value: number): string => {
    if (value < 10000) return Math.round(value).toLocaleString('en-US');
    return formatNumber(value);
};

const hasFollowSplit = (value: unknown): boolean => {
    if (value == null || Array.isArray(value) || typeof value !== 'object') return false;
    const record = value as Record<string, unknown>;
    return Number.isFinite(Number(record.FOLLOWER)) || Number.isFinite(Number(record.NON_FOLLOWER));
};

const SocialAccountReelRates: React.FC<SocialAccountReelRatesProps> = ({ socialAccount }) => {
    const rates = useMemo((): ReelRate[] => {
        const accountId = socialAccount.uuid;
        return [
            {
                id: `reel-interaction-rate-${accountId}`,
                label: 'Interaction rate',
                value: formatRate(socialAccount.ig?.reels_interaction_rate),
                tooltip: 'Share of reel viewers who liked, commented, shared, or saved in the last 90 days.',
            },
            {
                id: `reel-hook-rate-${accountId}`,
                label: 'Hook rate',
                value: formatRate(socialAccount.ig?.reels_hook_rate),
                tooltip: 'Share of reel viewers who watched past the first 3 seconds in the last 90 days.',
            },
        ];
    }, [socialAccount]);

    const counts = useMemo((): ReelRate[] => {
        const ig = socialAccount.ig;
        const followType = ig?.insights?.follow_type;
        const followTypeRecord = followType && !Array.isArray(followType) ? followType : null;
        const accountId = socialAccount.uuid;
        const items: Array<ReelRate & { count: number | null; hasSplit: boolean }> = [
            {
                id: `account-interactions-count-${accountId}`,
                label: 'Account interactions',
                value: null,
                tooltip: 'Total account interactions in the last 30 days.',
                count: parseCount(ig?.account_interactions_count),
                hasSplit: hasFollowSplit(followTypeRecord?.account_interactions),
            },
            {
                id: `views-count-${accountId}`,
                label: 'Total views',
                value: null,
                tooltip: 'Total views in the last 30 days.',
                count: parseCount(ig?.views_count),
                hasSplit: hasFollowSplit(followTypeRecord?.views),
            },
        ];

        return items.flatMap((item) => {
            if (item.count == null || item.hasSplit) return [];
            return [{ id: item.id, label: item.label, value: formatCount(item.count), tooltip: item.tooltip }];
        });
    }, [socialAccount]);

    const rows = [rates.filter((rate) => rate.value), counts].filter((row) => row.length > 0);

    if (rows.length === 0) return null;

    return (
        <div className="social-account-reel-rates">
            {rows.map((row) => (
                <div className="social-account-reel-rates__row" key={row.map((item) => item.id).join('-')}>
                    {row.map((rate) => (
                        <div className="social-account-reel-rates__item" key={rate.id}>
                            <p className="social-account-reel-rates__value">{rate.value}</p>
                            <div className="social-account-reel-rates__label-row">
                                <p className="social-account-reel-rates__label">{rate.label}</p>
                            </div>
                            <p className="social-account-reel-rates__period">
                                {rate.id.startsWith('reel-') ? 'Last 90 days' : 'Last 30 days'}
                            </p>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default SocialAccountReelRates;
